/**
 * Typed wrappers for the dashboard's conversation endpoints.
 *
 *   GET /api/platform/conversation   current session's history
 *
 * Future PRs:
 *   POST /api/platform/conversation/ask
 *   POST /api/platform/conversation/reset
 *   ...
 */
import { api } from './client';

/** A single source / citation attached to an assistant turn.
 *
 *  Two flavours after PR #127 (search-time citation mixer):
 *    - `type: 'document'` (default when absent): the legacy doc
 *      citation. Renders as a chip in MessageSources.
 *    - `type: 'video'`: a transcript-matched video segment.
 *      Renders as a thumbnail tile beneath the message with an
 *      inline player.
 *
 *  Extra keys carried for video citations are documented inline.
 *  Doc citations carry the legacy id/title/source_file/snippet
 *  surface and ignore the video-specific fields. */
export interface MessageSource {
  /** Citation type. Absent on older persisted turns; treat as
   *  'document' when missing for backward compatibility. */
  type?: 'document' | 'video';
  id: string;
  title: string;
  snippet?: string;
  source_file?: string;
  /** Legacy / doc-only relevance score. */
  relevance?: number;
  document_type?: string;

  // ── video-citation extras (only when type === 'video') ──
  /** Stable id of the source video. Used to refresh signed URLs
   *  past their 1h expiry via /api/platform/videos/<id>/play-url. */
  video_id?: string;
  course_title?: string | null;
  course_id?: string | null;
  /** Seconds offset into the video where the matched chunk starts. */
  start_time?: number;
  /** Pre-formatted "m:ss" / "h:mm:ss" string for the timestamp badge. */
  start_time_display?: string;
  end_time?: number | null;
  chunk_index?: number;
  /** 1h-signed GCS URL for the source video. The inline player
   *  uses `${play_url}#t=${start_time}` so the browser seeks
   *  natively without JS. */
  play_url?: string | null;
  /** 1h-signed URL for the chunk's thumbnail. Null when no
   *  thumbnail was rendered (ffmpeg failure, etc.). */
  thumb_url?: string | null;
  has_thumb?: boolean;
}

/** Static "resource" attached to one or more cited documents. Phase 3
 *  of the Resources feature surfaces these at the BOTTOM of an
 *  assistant message, below the answer text, separate from `sources`
 *  at the top. Two kinds:
 *    - 'url'  → external link. Renders as a pill that opens in a new
 *               tab. No platform-side fetch needed.
 *    - 'file' → server-stored upload. The pill opens
 *               /api/resources/<id>/file in a new tab; the platform
 *               session cookie carries auth, so a plain <a href>
 *               works (unlike the widget which uses Bearer tokens).
 */
export interface MessageResource {
  id: string;
  title: string;
  kind: 'url' | 'file';
  /** Set when kind === 'url'. */
  url?: string;
  /** Set when kind === 'file'. */
  mime_type?: string;
  original_filename?: string;
  byte_size?: number;
}

/** A single conversation turn. Matches the dict shape that
 *  chat_service appends to session['conversation_history']. */
export interface ConversationTurn {
  role: 'user' | 'assistant';
  content: string;
  /** ISO timestamp set when the turn was appended. May be missing
   *  on very old sessions. */
  timestamp?: string;
  /** Image attachments - only on user turns. Resolves via
   *  /api/attachment/<id>. */
  attachment_ids?: string[];
  /** Citations - only on assistant turns. */
  sources?: MessageSource[];
  /** Related resources attached to the cited documents - only on
   *  assistant turns. Phase 3 of the Resources feature; persisted
   *  on the turn so a session reload keeps the bottom-of-message
   *  row in place. */
  resources?: MessageResource[];
  /** TTS audio URL (server-relative, e.g. /audio/x.mp3) for
   *  assistant turns where the user asked for spoken replies.
   *  Persisted server-side onto the turn so the SPA's
   *  AudioPlayer + per-turn stop button can bind playback to
   *  this specific bubble via turn.timestamp. */
  audio_url?: string | null;
  /** Set on assistant turns that asked the user "want me to
   *  answer from general knowledge?". PR-D4 wires the Yes/No
   *  buttons that respond to this. */
  awaiting_fallback_decision?: boolean;
  /** Chat-log id of this turn, on assistant turns. Used to attach
   *  thumbs up/down feedback via POST /api/platform/feedback. */
  messageId?: string;
  /** Server-side debug info on assistant turns. Not displayed -
   *  carried for completeness so the SPA can render server-known
   *  state when needed. */
  search_info?: {
    query?: string;
    total_results?: number;
    documents_used?: string[];
  };
}

export interface ConversationResponse {
  turns: ConversationTurn[];
  /** Empty string when no thread has been promoted yet (fresh
   *  session). Future-PR features (e.g. share-this-thread) will
   *  use this. */
  thread_id: string;
}

export function getConversation(): Promise<ConversationResponse> {
  return api<ConversationResponse>('/api/platform/conversation');
}

// ── Mutations ───────────────────────────────────────────────────────
// We POST to the existing legacy /api/chat and /api/conversation/reset
// endpoints rather than wrap them under /api/platform/* - same logic
// in the SAME chat_service.process_chat that the legacy main.js drove.
// Wrapping would just be cruft and risk drift. Noted here so a future
// reader knows why these calls cross the namespace boundary.

export type ResponseLength = 'standard' | 'long';

export interface AskRequest {
  /** The user's question. Trimmed before send. */
  message: string;
  /** Standard | Detailed - segmented control on the rail. */
  response_length: ResponseLength;
  /** Image attachment ids from /api/attachment/upload. Server
   *  passes them to the multimodal model and stamps them onto
   *  the user turn so the bubble renders thumbnails. */
  attachment_ids?: string[];
  /** When true, server generates TTS audio and returns
   *  `audio_url` on the response. Set by the audio-reply
   *  toggle in the composer. */
  audio_requested?: boolean;
  /** Restrict the search to these Vertex doc ids. Empty / omitted
   *  means "all documents the tenant has". Driven by the rail's
   *  document filter; group selections are pre-expanded into
   *  their constituent doc ids before the request. */
  document_ids?: string[];
  /** ISO 639-1 code (e.g. 'en', 'es') OR empty string for
   *  auto-detect. Driven by the rail's language picker. */
  language?: string;
}

/** Single-turn response shape from /api/chat. The full updated
 *  conversation lives in session['conversation_history']; the
 *  caller invalidates the conversation query to pick that up. */
export interface AskResponse {
  response: string;
  sources?: MessageSource[];
  /** Resources attached to any of the cited documents. Absent when
   *  none are attached (or the server hasn't been upgraded to
   *  Phase 3 yet — treat as []). */
  resources?: MessageResource[];
  audio_url?: string | null;
  /** When true, the assistant's reply was a "want me to answer
   *  from general knowledge?" prompt - Yes/No buttons render
   *  under it. The next POST carries `fallback_decision`. */
  awaiting_fallback_decision?: boolean;
  /** Chat-log id of this turn, for attaching answer feedback. */
  messageId?: string;
}

export function askQuestion(req: AskRequest): Promise<AskResponse> {
  return api<AskResponse>('/api/chat', {
    method: 'POST',
    body: req,
  });
}

// ── Answer feedback (thumbs) ────────────────────────────────────────

export type FeedbackRating = 'up' | 'down';

/** Record (or clear) thumbs up/down on an assistant answer. Pass
 *  rating '' to clear a previous rating. Session-auth only, like the
 *  rest of platform-ui. */
export function submitFeedback(
  messageId: string,
  rating: FeedbackRating | '',
): Promise<{ ok: boolean }> {
  return api<{ ok: boolean }>('/api/platform/feedback', {
    method: 'POST',
    body: { messageId, rating },
  });
}

/** Optimistic source preview - runs Vertex search only, returns
 *  matched docs in ~500ms. The dashboard fires this in parallel
 *  with askQuestion() so the ThinkingIndicator can render
 *  "Found in foo.docx, bar.pdf" the moment the search settles,
 *  without waiting for the full Gemini round-trip.
 *
 *  Same security posture as askQuestion (login_required, scoped
 *  to the active client). The server returns an empty array on
 *  any failure rather than 500-ing - preview is best-effort
 *  cosmetic; the real ask is the source of truth. */
export function previewSources(req: {
  message: string;
  document_ids?: string[];
  language?: string;
}): Promise<{ sources: MessageSource[] }> {
  return api<{ sources: MessageSource[] }>('/api/chat/preview', {
    method: 'POST',
    body: req,
  });
}

/** Resolve a previously-asked offer-AI-fallback prompt. The server
 *  reads the original query from session['conversation_history'] -
 *  no message is sent. */
export function sendFallbackDecision(
  decision: 'accept' | 'decline',
): Promise<AskResponse> {
  return api<AskResponse>('/api/chat', {
    method: 'POST',
    body: { fallback_decision: decision },
  });
}

/** Clear session['conversation_history'] + session['thread_id'] so
 *  the next message starts a fresh thread. */
export function resetConversation(): Promise<{ status: string }> {
  return api<{ status: string }>('/api/conversation/reset', {
    method: 'POST',
  });
}
